// Sign-in screen for the admin app.

import { useState } from 'react';
import { Loader2, LogIn } from 'lucide-react';
import { useAuth } from '../lib/auth';

export default function LoginPage() {
    const { signIn } = useAuth();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy) return;
        setError(null);
        setBusy(true);
        try {
            await signIn(username.trim(), password);
            // On success the provider swaps this screen out; no navigation here.
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Sign in failed');
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-slate-900 px-4">
            <div className="w-full max-w-sm">
                <div className="flex flex-col items-center mb-8">
                    <div className="w-16 h-16 flex items-center justify-center">
                        <img src="/rohan-logo.svg" alt="Rohan" className="w-16 h-16 object-contain brightness-0 dark:brightness-100" />
                    </div>
                    <h1 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
                        Site Smart
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Sign in to continue
                    </p>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 space-y-4"
                >
                    <div>
                        <label
                            htmlFor="username"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                        >
                            Username
                        </label>
                        <input
                            id="username"
                            name="username"
                            autoComplete="username"
                            autoFocus
                            required
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-indigo-400 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="password"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                        >
                            Password
                        </label>
                        <input
                            id="password"
                            name="password"
                            type="password"
                            autoComplete="current-password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-indigo-400 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                        />
                    </div>

                    {error && (
                        <p
                            role="alert"
                            className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 rounded-md px-3 py-2"
                        >
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={busy || !username || !password}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {busy ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
                        {busy ? 'Signing in…' : 'Sign in'}
                    </button>
                </form>
            </div>
        </div>
    );
}
